import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowDown, ChevronLeft, ChevronDown,
  CheckCircle2, Sparkles, Calendar, Clock, Users, Video,
  Heart, Wind, MessageCircle, Shield, Star, Phone, X,
} from 'lucide-react';
import api from '../lib/api';

const SLUG_FALLBACK = 'limites-2a-edicion';
const WA_NUMBER = '34654827313';
const WA_BASE = `https://wa.me/${WA_NUMBER}`;
const waLink = (text) => `${WA_BASE}?text=${encodeURIComponent(text)}`;
const WA_CALL_TEXT = 'Hola Herminio, me gustaría agendar una llamada contigo para hablar sobre LÍMITES (2ª edición 2026).';

const HERO_PORTRAIT = 'https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto/v1788536434/ChatGPT_Image_4_sept_2026_17_39_19_mi6eg4.png';
const BIO_PORTRAIT = 'https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto,w_800/v1787843791/ChatGPT_Image_27_ago_2026_17_16_12_sy9ph1.png';

// ------- Sub-components ------------------------------------------------
const SectionEyebrow = ({ children }) => (
  <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#8a7428] font-medium mb-3">
    <span className="w-6 h-px bg-[#c9a227]/60" />
    {children}
    <span className="w-6 h-px bg-[#c9a227]/60" />
  </p>
);

const SectionTitle = ({ children, className = '' }) => (
  <h2 className={`font-heading text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.15] tracking-tight text-[hsl(var(--text-primary))] ${className}`}>
    {children}
  </h2>
);

const GoldDivider = () => (
  <div className="flex items-center justify-center gap-3 my-10 sm:my-14">
    <span className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent via-[#c9a227]/70 to-transparent" />
    <Sparkles className="w-4 h-4 text-[#c9a227]" />
    <span className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent via-[#c9a227]/70 to-transparent" />
  </div>
);

const CheckLi = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 mt-1 w-5 h-5 rounded-full bg-[#c9a227]/15 border border-[#c9a227]/40 flex items-center justify-center">
      <CheckCircle2 className="w-3 h-3 text-[#8a7428]" strokeWidth={2.5} />
    </span>
    <span className="text-[hsl(var(--text-primary))]/85 text-[15px] sm:text-base leading-[1.65]">{children}</span>
  </li>
);

const Faq = ({ q, a, isOpen, onToggle, testId }) => (
  <div className="border-b border-[#e5dcc4] py-5">
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between text-left gap-4"
      data-testid={testId}
      aria-expanded={isOpen}
    >
      <span className="font-heading text-lg sm:text-xl text-[hsl(var(--text-primary))] leading-snug">{q}</span>
      <ChevronDown className={`w-5 h-5 text-[#8a7428] flex-shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="mt-3 pr-8 text-[15px] leading-[1.7] text-[hsl(var(--text-secondary))] whitespace-pre-line">{a}</div>
    )}
  </div>
);

// Floating WhatsApp button — appears after user scrolls a bit.
// Positioned above mobile sticky CTA so it never overlaps price or main button.
const WhatsAppFloat = ({ waHref }) => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <div
      className="fixed right-4 sm:right-6 z-[55] flex flex-col items-end gap-3"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}
    >
      {expanded && (
        <div className="bg-white rounded-2xl shadow-2xl border border-[#e5dcc4] p-4 max-w-[280px] animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="font-heading text-base text-[hsl(var(--text-primary))] leading-tight">¿Tienes dudas?</p>
            <button
              onClick={() => setExpanded(false)}
              className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))]"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[13px] text-[hsl(var(--text-secondary))] leading-relaxed mb-3">
            Escríbeme por WhatsApp y agendamos una llamada para hablar sobre LÍMITES sin compromiso.
          </p>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20b658] text-white px-4 py-2 rounded-full text-sm font-medium transition-all w-full justify-center"
            data-testid="wa-float-cta"
          >
            <MessageCircle className="w-4 h-4" /> Agendar llamada
          </a>
        </div>
      )}
      <button
        onClick={() => setExpanded(v => !v)}
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20b658] text-white p-3 sm:pl-3.5 sm:pr-4 sm:py-3 rounded-full shadow-lg shadow-black/20 transition-transform hover:scale-105"
        data-testid="wa-float-button"
        aria-label="Abrir WhatsApp"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden lg:inline text-sm font-medium">Hablar por WhatsApp</span>
      </button>
    </div>
  );
};

// Testimonial card with mobile-collapsible full quote.
const TestimonialCard = ({ hero, full, author, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <blockquote className="bg-[#faf6ed] rounded-2xl border border-[#e5dcc4] p-6 sm:p-7 relative">
      <div className="flex gap-0.5 mb-3">
        {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-[#c9a227] text-[#c9a227]" />)}
      </div>
      {/* Frase protagonista, siempre visible */}
      <p className="font-[Cormorant_Garamond,serif] italic text-lg sm:text-xl leading-[1.4] text-[hsl(var(--text-primary))] mb-3">
        “{hero}”
      </p>
      {/* Fragmento — visible en desktop, oculto en móvil hasta expandir */}
      <div className={`${open ? 'block' : 'hidden'} sm:block`}>
        <p className="text-[14.5px] sm:text-[15px] leading-[1.7] text-[hsl(var(--text-secondary))] mb-4">
          {full}
        </p>
      </div>
      <div className="flex items-center justify-between gap-3">
        <cite className="not-italic text-[13px] font-medium text-[#8a7428]">— {author}</cite>
        <button
          onClick={() => setOpen(v => !v)}
          className="sm:hidden text-[12px] font-medium text-[#8a7428] underline decoration-[#c9a227] underline-offset-4"
          data-testid={testId}
        >
          {open ? 'Cerrar experiencia' : 'Leer experiencia completa'}
        </button>
      </div>
    </blockquote>
  );
};

// ------- Main --------------------------------------------------------
export default function CursoLimites2026() {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');
  // Dynamic slug resolution: on mount we look up the currently-open LÍMITES
  // special_course from the API. Whatever slug the admin has assigned to the
  // active edition (limites-2026, limites-2, etc.) will work automatically.
  const [checkoutSlug, setCheckoutSlug] = useState(SLUG_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    api.get('/special-courses')
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        // Find the active LÍMITES course — match by slug or title, prefer newest.
        const isLimites = (c) => {
          const s = (c.slug || '').toLowerCase();
          const t = (c.title || '').toLowerCase();
          return (s.includes('limites') || s.includes('límites') || t.includes('limites') || t.includes('límites'));
        };
        const isActive = (c) => (c.status || '').toLowerCase() === 'open' && c.landing_public !== false && c.is_published !== false;
        const candidates = list.filter(c => isLimites(c) && isActive(c));
        // Prefer the newest start_date (or created_at); exclude the OLD closed edition
        candidates.sort((a, b) => (b.start_date || b.created_at || '').localeCompare(a.start_date || a.created_at || ''));
        if (candidates.length > 0 && candidates[0].slug) {
          setCheckoutSlug(candidates[0].slug);
        }
      })
      .catch(() => { /* fallback to SLUG_FALLBACK */ });
    return () => { cancelled = true; };
  }, []);

  const navItems = [
    { id: 'hero', label: 'Inicio' },
    { id: 'para-quien', label: '¿Es para mí?' },
    { id: 'arquitectura', label: 'Arquitectura' },
    { id: 'temario', label: 'Recorrido' },
    { id: 'formato', label: 'Formato' },
    { id: 'inversion', label: 'Inversión' },
    { id: 'faq', label: 'FAQ' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 200;
      for (let i = navItems.length - 1; i >= 0; i--) {
        const el = document.getElementById(navItems[i].id);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 130, behavior: 'smooth' });
  };

  // Testimonios reales — frase protagonista + fragmento + autoría.
  const testimonios = [
    {
      hero: 'Ahora sé cuál es mi lugar en el vínculo.',
      full: 'Ha sido un viaje personal muy importante y decisivo. Poderoso, enriquecedor y muy liberador. Una herramienta de por vida con la cual seguir aprendiendo y creciendo como Alma y como humano. Ha hecho que mi mirada ahora sea más amplia, más clara y más amorosa en las relaciones.',
      a: 'Maite E.',
    },
    {
      hero: 'Fui encontrando ese yo verdadero que, sin darme cuenta, iba abandonando.',
      full: 'El cambio de comprensión y experiencia más importante ha sido darme cuenta, de una manera muy sensorial y conectando con algo propio, de componer límites desde un lugar que no me imaginaba en absoluto.',
      a: 'Rosabel A.',
    },
    {
      hero: 'Puedo relacionarme con los demás de forma madura.',
      full: 'Al hacerlo, aprendo a relacionarme también conmigo con pausa y con respeto.',
      a: 'María R.',
    },
    {
      hero: 'Espacios donde todas las almitas podamos ser, descubrirnos, encontrarnos.',
      full: 'Gracias por llevarme allí donde sola quizá no me atrevería o no sé ir, creando así nuevos encuentros con mi sabiduría y maestría, con lo que realmente somos.',
      a: 'M.',
    },
  ];

  const movimientos = [
    { n: '01', t: 'El origen del colapso del límite', d: 'Observar cómo aprendemos a salir de nuestro propio espacio para sostener vínculo, pertenencia o seguridad.' },
    { n: '02', t: 'Regresar al propio espacio', d: 'Reconocer el movimiento interno que permite volver a sentir dónde estás tú antes de reaccionar.' },
    { n: '03', t: 'Cuerpo, regulación y sistema nervioso', d: 'Qué ocurre cuando algo supera tu capacidad y el límite aparece desde tensión, bloqueo o huida.' },
    { n: '04', t: 'Campo propio, sensibilidad y energía', d: 'Qué sucede cuando sientes que absorbes, te vacías o pierdes energía en determinados encuentros.' },
    { n: '05', t: 'Culpa, autoexigencia y disponibilidad', d: 'Cuánto de lo que das nace de elección y cuánto aparece para evitar incomodidad o rechazo.' },
    { n: '06', t: 'Comunicación y vínculo', d: 'Expresar una necesidad, una decisión o un límite permaneciendo en contacto contigo y con el otro.' },
    { n: '07', t: 'Espiritualidad encarnada', d: 'Integrar cuerpo, energía y consciencia sin usar lo espiritual para permanecer donde te alejas de ti.' },
    { n: '08', t: 'Integración', d: 'Reconocer progresivamente tu propia forma de permanecer, regresar y cuidar tu espacio en la vida cotidiana.' },
  ];

  // Movimientos como acordeón en móvil (uno abierto a la vez).
  const [openMov, setOpenMov] = useState(null);

  const teoricoPracticas = [
    { mes: 'Octubre', dias: ['13', '20', '27'] },
    { mes: 'Noviembre', dias: ['10', '17', '24'] },
    { mes: 'Diciembre', dias: ['15', '22', '29'] },
  ];
  const circulos = [
    { mes: 'Octubre', dias: ['23'] },
    { mes: 'Noviembre', dias: ['13', '27'] },
    { mes: 'Diciembre', dias: ['18'] },
  ];

  const faqs = [
    { q: '¿El curso ya ha empezado?', a: 'El recorrido principal empieza el martes 13 de octubre. El 29 de septiembre celebramos la Clase 0, una sesión inicial destinada a observar desde dónde parte cada persona. Al inscribirte ahora tendrás acceso inmediato a su grabación dentro del campus.' },
    { q: '¿Qué tengo que hacer antes del 13 de octubre?', a: 'Cuando accedas al campus encontrarás la Clase 0. Te recomiendo verla con calma y realizar las propuestas de observación que contiene para llegar al primer encuentro habiendo identificado tu punto de partida.' },
    { q: '¿Qué ocurre si no puedo asistir a alguna sesión?', a: 'Los encuentros se graban y podrás acceder posteriormente a la grabación desde el campus. La experiencia en directo aporta una dimensión importante al proceso, especialmente en los círculos de integración, pero faltar puntualmente a un encuentro no impide continuar.' },
    { q: '¿Necesito experiencia previa en trabajo energético o desarrollo personal?', a: 'No. Trabajaremos los conceptos desde la experiencia, el cuerpo, el vínculo y situaciones de la vida cotidiana. Puedes llegar con experiencia previa o encontrarte por primera vez con este tipo de trabajo.' },
    { q: '¿Es terapia?', a: 'LÍMITES es un proceso formativo y experiencial de consciencia y desarrollo personal. No sustituye psicoterapia, tratamiento médico ni atención clínica cuando estos sean necesarios.' },
    { q: '¿Qué diferencia hay entre las sesiones de los martes y los círculos de los viernes?', a: 'Los martes contienen el recorrido principal del programa: comprensión, práctica, exploración y nuevas capas de trabajo.\n\nLos viernes son espacios de integración para observar qué está ocurriendo en tu vida real a partir de ese recorrido.' },
    { q: '¿Puedo pagar por Bizum o transferencia?', a: 'Sí. Puedes escribirme directamente por WhatsApp y te facilitaré los datos.' },
    { q: '¿Puedo hablar contigo antes de decidir?', a: 'Sí. Si hay algo concreto que necesitas aclarar antes de entrar, puedes escribirme directamente por WhatsApp.' },
  ];

  const waPagoTexto = 'Hola Herminio, me gustaría reservar mi plaza en LÍMITES (2ª edición 2026) y pagar por Bizum.';

  return (
    <>
      <Helmet>
        <title>LÍMITES · El espacio original de la consciencia (2ª edición) | Escuela de Consciencia Akáshica</title>
        <meta name="description" content="Un proceso online en directo de tres meses para recuperar tu referencia interna, comprender cómo funcionan tus límites y relacionarte sin desaparecer dentro del vínculo. Empezamos el martes 13 de octubre. Clase 0 disponible ya." />
        <meta property="og:title" content="LÍMITES · El espacio original de la consciencia (2ª edición)" />
        <meta property="og:description" content="1 Clase 0 disponible ya + 9 sesiones principales en directo + 4 círculos de integración. Empezamos el 13 de octubre." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Sticky secondary nav — fondo totalmente opaco para no dejar ver contenido por debajo */}
      <div className="sticky top-[72px] z-40 bg-[#fdfbf5] border-b border-[#e5dcc4] shadow-sm">
        <div className="max-w-6xl mx-auto pl-3 pr-3 sm:px-6">
          <div className="flex items-center h-14 gap-2">
            <Link
              to="/formaciones"
              className="flex items-center gap-1 text-xs sm:text-sm text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] flex-shrink-0"
              aria-label="Volver a formaciones"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Formaciones</span>
            </Link>
            <nav
              className="flex items-center gap-1 overflow-x-auto hide-scrollbar flex-1 justify-start sm:justify-center scroll-smooth"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className={`px-3 py-1.5 rounded-full text-[12px] sm:text-sm whitespace-nowrap transition-colors flex-shrink-0 ${activeSection === item.id ? 'bg-[#c9a227] text-white' : 'text-[hsl(var(--text-secondary))] hover:bg-[#c9a227]/10'}`}
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <button
              onClick={() => scrollTo('inversion')}
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#c9a227] hover:bg-[#b89220] text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all flex-shrink-0 shadow-sm"
              data-testid="nav-reservar-persistent"
            >
              Entrar en LÍMITES
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* WhatsApp float — appears after user scrolls a bit (positioned above sticky CTA) */}
      <WhatsAppFloat waHref={waLink(WA_CALL_TEXT)} />

      {/* Sticky bottom CTA — mobile only */}
      <div className="lg:hidden fixed left-0 right-0 bottom-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#e5dcc4] shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.1)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="text-[9px] tracking-[0.2em] uppercase text-[#8a7428] leading-none">13 OCT</div>
            <div className="text-[13px] font-medium text-[hsl(var(--text-primary))] leading-tight mt-0.5">Desde 1.119,25 €</div>
          </div>
          <button
            onClick={() => scrollTo('inversion')}
            className="flex-1 bg-[#c9a227] hover:bg-[#b89220] text-white text-[13px] font-medium rounded-full py-3 transition-all"
            data-testid="mobile-sticky-cta"
          >
            Entrar en LÍMITES
          </button>
        </div>
      </div>

      {/* ============ 1. HERO ============ */}
      <section
        id="hero"
        className="relative min-h-[calc(100vh-136px)] flex items-center overflow-hidden px-4 sm:px-6 pt-24 sm:pt-20 pb-14 sm:pb-20 bg-[#0f1a26]"
      >
        {/* Ambient dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1420] via-[#101c2b] to-[#1a2332]" aria-hidden="true" />
        {/* Soft warm glow on the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(60% 55% at 80% 40%, rgba(201,162,39,0.14) 0%, rgba(201,162,39,0) 60%), radial-gradient(45% 55% at 15% 90%, rgba(201,162,39,0.08) 0%, rgba(201,162,39,0) 55%)',
          }}
          aria-hidden="true"
        />
        {/* Geometric sphere network on the right (poster style) */}
        <svg
          className="absolute top-1/2 -translate-y-1/2 right-[-8%] w-[70%] max-w-[900px] opacity-[0.18] pointer-events-none text-[#e8c75a]"
          viewBox="0 0 500 500"
          fill="none"
          aria-hidden="true"
        >
          <g stroke="currentColor" strokeWidth="0.6" fill="none">
            <circle cx="250" cy="250" r="200" />
            <ellipse cx="250" cy="250" rx="200" ry="65" />
            <ellipse cx="250" cy="250" rx="200" ry="130" />
            <ellipse cx="250" cy="250" rx="65" ry="200" />
            <ellipse cx="250" cy="250" rx="130" ry="200" />
            <ellipse cx="250" cy="250" rx="200" ry="200" transform="rotate(30 250 250)" />
            <ellipse cx="250" cy="250" rx="200" ry="80" transform="rotate(30 250 250)" />
            <ellipse cx="250" cy="250" rx="200" ry="80" transform="rotate(60 250 250)" />
            <ellipse cx="250" cy="250" rx="200" ry="80" transform="rotate(-30 250 250)" />
            <ellipse cx="250" cy="250" rx="200" ry="80" transform="rotate(-60 250 250)" />
          </g>
          {[
            [250, 50], [250, 450], [50, 250], [450, 250],
            [110, 110], [390, 110], [110, 390], [390, 390],
            [250, 180], [250, 320], [180, 250], [320, 250],
            [180, 180], [320, 320], [180, 320], [320, 180],
          ].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r="2.2" fill="currentColor" />
          ))}
        </svg>
        {/* Portrait on the right — desktop only, blended */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 hidden lg:block z-[2]"
          style={{ width: 'min(48%, 660px)' }}
          aria-hidden="true"
        >
          <img
            src={HERO_PORTRAIT}
            alt=""
            className="w-full h-full object-cover object-top"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 22%, rgba(0,0,0,0.9) 55%, rgba(0,0,0,1) 100%)',
              maskImage:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 22%, rgba(0,0,0,0.9) 55%, rgba(0,0,0,1) 100%)',
            }}
            loading="eager"
            data-testid="hero-portrait"
          />
        </div>

        <div className="relative z-10 max-w-6xl w-full mx-auto">
          <div className="max-w-2xl">
            {/* Key facts strip — top chips (móvil: cada chip ocupa su ancho, sin cortes) */}
            <div className="flex flex-wrap gap-2 mb-6 max-w-full">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 backdrop-blur-sm border border-white/15 rounded-full text-[12.5px] sm:text-[13px] text-white/90 whitespace-nowrap">
                <Video className="w-3.5 h-3.5 text-[#e8c75a]" /> Online en directo
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 backdrop-blur-sm border border-white/15 rounded-full text-[12.5px] sm:text-[13px] text-white/90 whitespace-nowrap">
                <Calendar className="w-3.5 h-3.5 text-[#e8c75a]" /> Martes · 17:30–20:30 h
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c9a227]/25 backdrop-blur-sm border border-[#c9a227]/60 rounded-full text-[12.5px] sm:text-[13px] text-white font-medium whitespace-nowrap">
                <Clock className="w-3.5 h-3.5 text-[#e8c75a]" /> Empezamos el 13 de octubre
              </span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white mb-4">
              LÍMITES
            </h1>
            <p className="font-[Cormorant_Garamond,serif] italic text-2xl sm:text-3xl lg:text-[2.25rem] text-[#e8c75a] mb-6 leading-[1.2]">
              El espacio original de la consciencia
            </p>
            <p className="text-white/85 text-lg leading-relaxed mb-4 max-w-xl">
              Aprende a permanecer en tu propio espacio cuando estás con otras personas, incluso cuando lo que sienten, necesitan o esperan de ti te afecta profundamente.
            </p>
            <p className="text-white/70 text-[15px] leading-relaxed mb-8 max-w-xl">
              Un proceso online en directo de tres meses para recuperar tu referencia interna, comprender cómo funcionan tus límites y relacionarte sin desaparecer dentro del vínculo.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => scrollTo('inversion')}
                className="inline-flex items-center justify-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-4 rounded-full font-medium text-sm sm:text-base transition-all shadow-lg shadow-[#c9a227]/25 hover:-translate-y-0.5"
                data-testid="hero-cta-primary"
              >
                Entrar en LÍMITES <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollTo('inversion')}
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/8 text-white/90 px-8 py-4 rounded-full font-medium text-sm sm:text-base transition-all border border-white/25"
                data-testid="hero-cta-secondary"
              >
                Ver fechas e inversión <ArrowDown className="w-4 h-4" />
              </button>
            </div>

            <p className="mt-5 text-xs sm:text-sm text-[#e8c75a]/85">
              Clase 0 disponible ahora · Primer encuentro oficial en directo: 13 de octubre
            </p>
            <p className="mt-2 text-xs sm:text-sm text-white/50">
              ¿Ya formas parte de LÍMITES? <Link to="/login" className="underline decoration-[#c9a227] underline-offset-4 hover:text-white transition-colors">Acceder al campus →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* ============ 2. BANDA DE DECISIÓN RÁPIDA ============ */}
      <section className="bg-white border-b border-[#e5dcc4] py-8 sm:py-10 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-center">
          {[
            { big: '13 OCT', small: 'Inicio oficial' },
            { big: '14', small: 'Encuentros durante el proceso' },
            { big: 'ONLINE', small: 'Directo + grabaciones' },
            { big: '1.119,25 €', small: 'IVA incluido · pago único' },
          ].map((k, i) => (
            <div key={i} className="text-center border-r last:border-r-0 border-[#e5dcc4]/60">
              <div className="font-heading text-2xl sm:text-3xl lg:text-[2.25rem] text-[hsl(var(--text-primary))] leading-none">{k.big}</div>
              <div className="mt-2 text-[11px] sm:text-[12px] uppercase tracking-wider text-[#8a7428]">{k.small}</div>
            </div>
          ))}
        </div>
        <div className="text-center mt-6">
          <button onClick={() => scrollTo('inversion')} className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all shadow-md hover:-translate-y-0.5" data-testid="banda-cta">
            Entrar en LÍMITES <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ============ 3. ACCESO INMEDIATO A LA CLASE 0 ============ */}
      <section className="bg-[#faf6ed] py-14 sm:py-20 px-4 sm:px-6 border-b border-[#e5dcc4]">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c9a227]/15 border border-[#c9a227]/40 rounded-full text-[11px] tracking-[0.2em] uppercase text-[#8a7428] font-semibold mb-5">
            <Sparkles className="w-3.5 h-3.5" /> Ya puedes empezar
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight text-[hsl(var(--text-primary))] mb-5">
            La Clase cero ya está dentro del campus.
          </h2>
          <p className="text-[15.5px] sm:text-[17px] leading-[1.75] text-[hsl(var(--text-secondary))] max-w-2xl mx-auto mb-8">
            La Clase 0 de LÍMITES se celebró el 29 de septiembre y ya está disponible dentro del campus. Cuando te inscribas podrás acceder inmediatamente a ella, realizar tu primera observación de cómo estás viviendo hoy tus límites y llegar al 13 de octubre habiendo comenzado ya tu propio proceso.
          </p>
          <button onClick={() => scrollTo('inversion')} className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-6 py-3 rounded-full text-sm sm:text-base font-medium transition-all shadow-md hover:-translate-y-0.5" data-testid="clase0-cta">
            Entrar en LÍMITES y ver la Clase 0 <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ============ 4. RECONOCIMIENTO COTIDIANO ============ */}
      <section className="bg-white py-16 sm:py-24 px-4 sm:px-6 border-b border-[#e5dcc4]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <SectionEyebrow>Quizá te resulte familiar</SectionEyebrow>
            <SectionTitle>Quizá hasta ahora no habías llamado "límites" a todo esto</SectionTitle>
          </div>
          <ol className="space-y-6">
            {[
              'Después de determinados encuentros necesitas estar sola para volver a sentirte tú.',
              'Una conversación puede seguir dentro de tu cabeza durante horas.',
              'Antes de tomar una decisión, aparece automáticamente cómo podría reaccionar otra persona.',
              'Dices que sí y poco después notas que tu cuerpo ya estaba diciendo que no.',
              'A veces sientes que absorbes demasiado de determinadas personas o ambientes.',
            ].map((s, i) => (
              <li key={i} className="flex gap-5 sm:gap-6 items-baseline pb-5 border-b border-[#efe6d1] last:border-b-0">
                <span className="font-heading text-lg sm:text-xl text-[#c9a227] tracking-widest flex-shrink-0 w-7">{String(i + 1).padStart(2, '0')}</span>
                <p className="font-[Cormorant_Garamond,serif] italic text-lg sm:text-xl leading-[1.55] text-[hsl(var(--text-primary))]">{s}</p>
              </li>
            ))}
          </ol>
          <div className="mt-10 max-w-2xl mx-auto text-center">
            <p className="text-[15px] sm:text-[16px] leading-[1.7] text-[hsl(var(--text-secondary))] mb-6">
              Quieres acompañar, amar, cuidar y permanecer disponible. Y algunas veces, casi sin darte cuenta, empiezas a organizarte alrededor de lo que ocurre fuera de ti.
            </p>
            <p className="font-heading text-xl sm:text-2xl text-[hsl(var(--text-primary))]">Todo esto también habla de límites.</p>
          </div>
        </div>
      </section>

      {/* ============ 5. ESPACIO ORIGINAL / NUEVA MIRADA (unificado, sustituye 4 bloques anteriores) ============ */}
      <section className="bg-[#f5efe6] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <SectionEyebrow>Una mirada nueva</SectionEyebrow>
          <SectionTitle className="mb-8">
            Antes de poner un límite hacia fuera, necesitas volver a sentir dónde estás tú.
          </SectionTitle>

          <p className="text-[15.5px] sm:text-[17px] leading-[1.8] text-[hsl(var(--text-secondary))] mb-6">
            Hay un espacio interno desde el que puedes percibir al otro y seguir sintiéndote.
          </p>

          <ul className="space-y-2.5 mb-8 text-[15px] sm:text-[16px] leading-[1.7] text-[hsl(var(--text-secondary))] pl-1">
            <li className="flex gap-3"><span className="text-[#c9a227] mt-1">·</span> Escuchar sin desaparecer.</li>
            <li className="flex gap-3"><span className="text-[#c9a227] mt-1">·</span> Acompañar sin cargar.</li>
            <li className="flex gap-3"><span className="text-[#c9a227] mt-1">·</span> Sentir profundamente sin convertir inmediatamente lo que percibes en algo que tienes que resolver.</li>
            <li className="flex gap-3"><span className="text-[#c9a227] mt-1">·</span> Dar sin vaciarte.</li>
            <li className="flex gap-3"><span className="text-[#c9a227] mt-1">·</span> Permanecer en una conversación incómoda sin abandonarte para poder seguir estando.</li>
            <li className="flex gap-3"><span className="text-[#c9a227] mt-1">·</span> Elegir sin que la reacción del otro se convierta automáticamente en tu referencia.</li>
          </ul>

          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-[#e5dcc4] p-6 sm:p-8 shadow-sm mb-6">
            <p className="text-[13px] tracking-[0.22em] uppercase text-[#8a7428] mb-3">A ese lugar lo llamamos en LÍMITES</p>
            <p className="font-heading text-2xl sm:text-3xl lg:text-[2.25rem] text-[hsl(var(--text-primary))] leading-[1.2] mb-4">
              El espacio original de la consciencia
            </p>
            <p className="text-[15px] sm:text-[16px] leading-[1.7] text-[hsl(var(--text-secondary))]">
              El lugar desde el que puedes seguir siendo tú mientras estás en relación con algo que te afecta.
            </p>
          </div>

          <p className="font-[Cormorant_Garamond,serif] italic text-lg sm:text-xl text-[#8a7428] leading-[1.55]">
            Durante el proceso observamos este límite desde el cuerpo, la regulación, la atención, el vínculo, la sensibilidad y el campo energético.
          </p>
        </div>
      </section>

      {/* ============ 6. ARQUITECTURA DEL LÍMITE ============ */}
      <section id="arquitectura" className="bg-[#0f2620] text-white py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-block text-[11px] tracking-[0.28em] uppercase text-[#e8c75a] mb-4">La arquitectura interna</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-[3rem] leading-[1.1] text-white mb-6">
              Tu límite tiene una arquitectura.
            </h2>
            <p className="max-w-2xl mx-auto text-white/80 text-[15.5px] sm:text-[17px] leading-[1.75]">
              Durante LÍMITES iremos observando cómo se organiza tu espacio interno cuando entras en relación con personas, emociones, ambientes y situaciones que te afectan. No necesitas memorizar una teoría. Necesitas empezar a reconocer qué ocurre dentro de ti.
            </p>
          </div>

          {/* Tres elementos centrales con más peso visual — sin jerarquía concéntrica cerrada */}
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
            {[
              { k: 'EJE', d: 'Desde dónde estás percibiendo y organizando lo que ocurre.' },
              { k: 'MEMBRANA', d: 'Cómo permanecer en contacto sin convertir automáticamente la experiencia del otro en la tuya.' },
              { k: 'ESPACIO INTERNO', d: 'Cuánto de ti sigue disponible cuando algo te afecta.' },
            ].map((n, i) => (
              <div key={i} className="rounded-2xl border border-[#e8c75a]/50 bg-[#e8c75a]/[0.06] p-6 sm:p-7">
                <div className="font-heading text-[16px] sm:text-[17px] tracking-[0.2em] text-[#e8c75a] mb-3">{n.k}</div>
                <p className="text-[14.5px] sm:text-[15px] leading-[1.7] text-white/85">{n.d}</p>
              </div>
            ))}
          </div>

          <details className="group max-w-4xl mx-auto">
            <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase text-[#e8c75a] hover:text-white transition-colors border-b border-[#e8c75a]/40 pb-1">
              <span>Ver arquitectura completa</span>
              <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[
                { k: 'ATENCIÓN', d: 'Dónde está tu consciencia mientras estás en relación.' },
                { k: 'DIFERENCIACIÓN', d: 'Reconocer qué pertenece a tu experiencia y qué pertenece al otro.' },
                { k: 'CAPACIDAD', d: 'Cuánto puedes sentir sin necesitar cerrarte, huir o reaccionar.' },
                { k: 'VÍNCULO', d: 'Qué ocurre con tu propio espacio cuando entras en relación.' },
                { k: 'RECUPERACIÓN', d: 'Cómo vuelves a ti después de algo que te ha movilizado.' },
              ].map((n, i) => (
                <div key={i} className="rounded-xl border border-white/12 bg-white/[0.03] p-5">
                  <div className="font-heading text-[14px] tracking-[0.2em] text-[#e8c75a] mb-2">{n.k}</div>
                  <p className="text-[13.5px] leading-[1.65] text-white/75">{n.d}</p>
                </div>
              ))}
            </div>
          </details>
        </div>
      </section>

      {/* ============ 7. CLASE CERO · TIMELINE ============ */}
      <section className="bg-white py-16 sm:py-24 px-4 sm:px-6 border-b border-[#e5dcc4]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <SectionEyebrow>Punto de partida</SectionEyebrow>
            <SectionTitle>Clase cero · Tu punto de partida</SectionTitle>
            <p className="max-w-2xl mx-auto text-[15.5px] sm:text-[16px] leading-[1.7] text-[hsl(var(--text-secondary))] mt-5">
              El 29 de septiembre realizamos una sesión inicial para observar desde dónde empieza cada persona. Al entrar ahora puedes verla inmediatamente dentro del campus y realizar tu primera fotografía de cómo estás viviendo tus límites.
            </p>
          </div>

          {/* Timeline vertical */}
          <ol className="relative max-w-xl mx-auto pl-8 sm:pl-10">
            <span className="absolute left-[7px] sm:left-[11px] top-2 bottom-2 w-px bg-[#c9a227]/40" aria-hidden />
            {[
              { fecha: '29 SEPTIEMBRE', titulo: 'Clase 0', desc: 'Grabada · disponible ya', tone: 'past' },
              { fecha: 'HOY', titulo: 'Te inscribes', desc: 'Accedes al campus y puedes comenzar tu proceso', tone: 'now' },
              { fecha: '13 OCTUBRE · 17:30', titulo: 'Primer directo', desc: 'Nos encontramos oficialmente en directo', tone: 'future' },
            ].map((s, i) => (
              <li key={i} className={`relative ${i > 0 ? 'mt-8' : ''}`}>
                <span
                  className={`absolute -left-8 sm:-left-10 top-[7px] w-[15px] h-[15px] rounded-full border-2 border-white ${
                    s.tone === 'now' ? 'bg-[#c9a227] shadow-md shadow-[#c9a227]/50' : 'bg-[#c9a227]/60'
                  }`}
                  aria-hidden
                />
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#8a7428] font-semibold">{s.fecha}</div>
                <h4 className="mt-1 font-heading text-lg sm:text-xl text-[hsl(var(--text-primary))]">{s.titulo}</h4>
                <p className="mt-1 text-[14px] leading-[1.65] text-[hsl(var(--text-secondary))]">{s.desc}</p>
              </li>
            ))}
          </ol>

          <p className="mt-10 text-center text-[14.5px] italic text-[hsl(var(--text-tertiary))] max-w-lg mx-auto">
            Así llegas al primer encuentro oficial del 13 de octubre habiendo comenzado ya tu proceso.
          </p>
          <div className="mt-8 text-center">
            <button onClick={() => scrollTo('inversion')} className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-7 py-3.5 rounded-full text-sm sm:text-base font-medium transition-all shadow-md hover:-translate-y-0.5" data-testid="clase0-timeline-cta">
              Entrar en LÍMITES y ver la Clase 0 <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ============ 8. QUÉ TRABAJAREMOS (temario · movido arriba) ============ */}
      <section id="temario" className="bg-[#fdfbf5] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <SectionEyebrow>Qué trabajaremos</SectionEyebrow>
            <SectionTitle>Ocho movimientos, tres meses de proceso</SectionTitle>
          </div>

          {/* Desktop: lista simple con título + una frase. Móvil: acordeones */}
          <div className="space-y-3">
            {movimientos.map((m, i) => {
              const isOpen = openMov === i;
              return (
                <div key={i} className="bg-white rounded-xl border border-[#e5dcc4] overflow-hidden">
                  {/* Móvil: header clickable, desktop: fila fija */}
                  <button
                    onClick={() => setOpenMov(isOpen ? null : i)}
                    className="w-full flex items-start gap-4 sm:gap-6 p-5 sm:p-6 text-left sm:cursor-default"
                    data-testid={`mov-${i}`}
                  >
                    <span className="flex-shrink-0 font-heading text-3xl sm:text-4xl text-[#c9a227] leading-none w-12 sm:w-14">{m.n}</span>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg sm:text-xl text-[hsl(var(--text-primary))] leading-snug mb-1">{m.t}</h3>
                      {/* Descripción: siempre visible en desktop; en móvil, solo si acordeón abierto */}
                      <p className={`${isOpen ? 'block' : 'hidden'} sm:block text-[14px] sm:text-[15px] text-[hsl(var(--text-secondary))] leading-[1.65]`}>
                        — {m.d}
                      </p>
                    </div>
                    <ChevronDown className={`sm:hidden w-4 h-4 text-[#8a7428] mt-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 9. FORMATO + CALENDARIO ============ */}
      <section id="formato" className="bg-white py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <SectionEyebrow>Formato y fechas</SectionEyebrow>
            <SectionTitle>13 oct. – 29 dic. 2026</SectionTitle>
            <div className="flex flex-wrap justify-center gap-2 mt-4 max-w-full">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#faf6ed] rounded-full text-[12.5px] sm:text-[13px] text-[#3a2e14] border border-[#e5dcc4] whitespace-nowrap">
                <Clock className="w-3.5 h-3.5 text-[#8a7428]" /> 3 meses · 14 encuentros
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#faf6ed] rounded-full text-[12.5px] sm:text-[13px] text-[#3a2e14] border border-[#e5dcc4] whitespace-nowrap">
                <Video className="w-3.5 h-3.5 text-[#8a7428]" /> Online en directo · con grabación
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c9a227]/15 border border-[#c9a227]/40 rounded-full text-[12.5px] sm:text-[13px] text-[#8a7428] font-medium whitespace-nowrap">
                <Sparkles className="w-3.5 h-3.5" /> Clase 0 · 29 sep · disponible ya
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-10">
            {/* Sesiones teórico-prácticas */}
            <div className="bg-[#faf6ed] rounded-2xl border border-[#e5dcc4] p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-9 h-9 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                  <Video className="w-4 h-4 text-[#8a7428]" />
                </span>
                <h3 className="font-heading text-xl text-[hsl(var(--text-primary))]">Sesiones teórico-prácticas</h3>
              </div>
              <p className="text-[14.5px] text-[hsl(var(--text-secondary))] mb-4">Activaciones energéticas, dinámicas y teoría.</p>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-white rounded-full border border-[#e5dcc4] text-[13px] font-medium text-[#8a7428]">
                <Clock className="w-3.5 h-3.5" /> Martes · 17:30 – 20:30h (España)
              </div>
              <div className="space-y-3">
                {teoricoPracticas.map((m, i) => (
                  <div key={i}>
                    <p className="text-[11px] uppercase tracking-wider text-[#8a7428] font-medium mb-1.5">{m.mes} 2026</p>
                    <div className="flex flex-wrap gap-1.5">
                      {m.dias.map((d, k) => (
                        <span key={k} className="inline-flex items-center justify-center min-w-[36px] px-2 py-1 bg-white border border-[#e5dcc4] rounded-md text-sm font-medium text-[hsl(var(--text-primary))]">
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 pt-4 border-t border-[#e5dcc4] text-xs text-[#8a7428]">9 sesiones principales · Grabación disponible</p>
            </div>

            {/* Círculos de integración */}
            <div className="bg-[#faf6ed] rounded-2xl border border-[#e5dcc4] p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-9 h-9 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                  <Heart className="w-4 h-4 text-[#8a7428]" />
                </span>
                <h3 className="font-heading text-xl text-[hsl(var(--text-primary))]">Círculos de integración</h3>
              </div>
              <p className="text-[14.5px] text-[hsl(var(--text-secondary))] mb-4">Encuentros para integrar en grupo lo vivido en las sesiones principales.</p>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-white rounded-full border border-[#e5dcc4] text-[13px] font-medium text-[#8a7428]">
                <Clock className="w-3.5 h-3.5" /> Viernes · desde las 18:00h (~2h)
              </div>
              <div className="space-y-3">
                {circulos.map((m, i) => (
                  <div key={i}>
                    <p className="text-[11px] uppercase tracking-wider text-[#8a7428] font-medium mb-1.5">{m.mes} 2026</p>
                    <div className="flex flex-wrap gap-1.5">
                      {m.dias.map((d, k) => (
                        <span key={k} className="inline-flex items-center justify-center min-w-[36px] px-2 py-1 bg-white border border-[#e5dcc4] rounded-md text-sm font-medium text-[hsl(var(--text-primary))]">
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 pt-4 border-t border-[#e5dcc4] text-xs text-[#8a7428]">4 círculos de integración · Grabación disponible</p>
            </div>
          </div>

          {/* Incluye */}
          <div className="bg-gradient-to-br from-[#faf6ed] via-[#f9f2df] to-[#f5ecd2] border border-[#e8d9a8] rounded-2xl p-6 sm:p-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#8a7428] font-medium mb-2">Todo lo que incluye tu inscripción</p>
            <h3 className="font-heading text-2xl text-[hsl(var(--text-primary))] mb-5">Un espacio completo durante los tres meses</h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {[
                'Clase 0 disponible inmediatamente',
                '9 sesiones teórico-prácticas en directo',
                '4 círculos de integración',
                'Grabaciones de todos los encuentros',
                'Prácticas guiadas y audios de integración',
                'Campus del alumnado con todo el material',
                'Acompañamiento grupal durante los tres meses',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-[#e5dcc4]/60">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#8a7428] flex-shrink-0" strokeWidth={2} />
                  <span className="text-[14.5px] text-[hsl(var(--text-primary))]">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ 10. DENTRO DE TRES MESES — Destino (texto nuevo) ============ */}
      <section className="bg-gradient-to-br from-[#0f1a26] via-[#151f2d] to-[#1a2332] py-16 sm:py-24 px-4 sm:px-6 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#e8c75a] font-medium mb-4">
            <span className="w-6 h-px bg-[#c9a227]/60" /> Dentro de tres meses <span className="w-6 h-px bg-[#c9a227]/60" />
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-[2.5rem] leading-[1.2] text-white mb-8">
            Imagina un día cualquiera de esa vida
          </h2>
          <div className="space-y-5 text-left sm:text-center text-white/85 text-[15.5px] sm:text-[17px] leading-[1.8]">
            <p>Alguien te pide algo que no puedes dar y puedes reconocer lo que necesitas antes de pensar qué espera de ti.</p>
            <p>Una conversación incómoda ya no tiene por qué ocupar todo tu espacio.</p>
            <p>Puedes sentir mucho sin asumir automáticamente que todo lo que sientes te pertenece.</p>
            <p>Puedes decir sí o no desde elección.</p>
            <p>Y cuando sales de tu eje, puedes reconocerlo y volver.</p>
          </div>
          <p className="font-[Cormorant_Garamond,serif] italic text-2xl sm:text-3xl lg:text-[2.25rem] text-[#e8c75a] leading-[1.25] mt-10">
            Poder estar en relación sin dejar de estar contigo.
          </p>
        </div>
      </section>

      {/* ============ 11. TESTIMONIOS ============ */}
      <section className="bg-white py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center">
            <SectionEyebrow>Voces de la primera edición</SectionEyebrow>
            <SectionTitle className="mb-12">Lo que otras personas han vivido</SectionTitle>
          </div>
          <div className="grid md:grid-cols-2 gap-5 sm:gap-7">
            {testimonios.map((t, i) => (
              <TestimonialCard
                key={i}
                hero={t.hero}
                full={t.full}
                author={t.a}
                testId={`testimonio-toggle-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ 12. QUIÉN ACOMPAÑA · HERMINIO ============ */}
      <section className="bg-[#f5efe6] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-8 lg:gap-14 items-start">
          <div className="lg:sticky lg:top-32">
            <div className="rounded-2xl overflow-hidden ring-1 ring-[#e5dcc4] shadow-lg">
              <img src={BIO_PORTRAIT} alt="Herminio Vázquez" className="w-full h-auto block" loading="lazy" />
            </div>
            <p className="mt-4 font-heading text-2xl text-center text-[hsl(var(--text-primary))]">Herminio Vázquez</p>
            <p className="text-center text-sm text-[#8a7428] italic">Fundador · Escuela de Consciencia Akáshica</p>
          </div>

          <div>
            <SectionEyebrow>Quién acompaña</SectionEyebrow>
            <SectionTitle className="mb-6">Soy Herminio Vázquez</SectionTitle>
            <div className="space-y-4 text-[15px] leading-[1.75] text-[hsl(var(--text-secondary))]">
              <p>Soy Herminio Vázquez, fundador de la Escuela de Consciencia Akáshica. Mi formación inicial fue la Arquitectura Técnica. Durante años aprendí a observar estructuras, espacios y aquello que, aunque no siempre resulta visible, sostiene todo lo que ocurre dentro de ellos.</p>
              <p>Con el tiempo, esa mirada empezó a dirigirse hacia el ser humano. A través del trabajo con consciencia, energía, cuerpo y años acompañando a personas sensibles fui observando algo que se repetía: muchas podían sentir con enorme claridad lo que ocurría fuera de ellas y, en determinados vínculos, les costaba mucho más <em className="not-italic text-[hsl(var(--text-primary))] font-medium">seguir sintiéndose a sí mismas</em>.</p>
              <p>LÍMITES nace también de esa observación. De empezar a comprender el límite como una arquitectura interna que permite entrar en relación sin desaparecer dentro de ella.</p>
              <div className="mt-6 flex items-center gap-4 py-4 border-y border-[#e5dcc4]">
                <span className="text-[13px] tracking-[0.15em] uppercase text-[#8a7428] font-medium">Arquitectura Técnica</span>
                <ArrowRight className="w-4 h-4 text-[#c9a227]" />
                <span className="text-[13px] tracking-[0.15em] uppercase text-[#8a7428] font-medium">Arquitectura interna</span>
              </div>
              <p className="text-[13.5px] text-[hsl(var(--text-tertiary))] italic">
                Formación y recorrido: Arquitectura Técnica, Feng Shui, Reiki, Registros Akáshicos, canalización, biodescodificación y acompañamiento energético y espiritual.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 13. PARA QUIÉN ES + CUÁNDO OTRO ACOMPAÑAMIENTO (movido abajo) ============ */}
      <section id="para-quien" className="bg-white py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="bg-[#faf6ed] rounded-2xl p-6 sm:p-8 border border-[#e5dcc4]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#8a7428]" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-2xl text-[hsl(var(--text-primary))]">Este proceso puede ser para ti si…</h3>
            </div>
            <ul className="space-y-3">
              {[
                'Sientes profundamente lo que sucede a tu alrededor.',
                'Te agotas después de determinados vínculos o ambientes.',
                'Te cuesta distinguir qué parte de lo que estás sintiendo pertenece a ti.',
                'Tiendes a adaptarte antes de comprobar qué necesitas.',
                'Te cuesta mantener una decisión cuando el otro se decepciona, se enfada o se distancia.',
                'Quieres relacionarte sin tener que cerrarte, aislarte o desaparecer dentro del vínculo.',
              ].map((t, i) => <CheckLi key={i}>{t}</CheckLi>)}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#e5dcc4]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#8a7428]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#8a7428]" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-2xl text-[hsl(var(--text-primary))]">Cuándo quizá necesites otro tipo de acompañamiento</h3>
            </div>
            <div className="space-y-4 text-[14.5px] leading-[1.7] text-[hsl(var(--text-secondary))]">
              <p>Si estás atravesando una crisis aguda de salud mental o una situación que requiere atención clínica individual, LÍMITES no sustituye ese acompañamiento.</p>
              <p>El proceso requiere cierta constancia y el trabajo se centra en tu propio espacio, no en cambiar a otra persona.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA WhatsApp — antes de Inversión ============ */}
      <section className="bg-[#faf6ed] py-12 sm:py-16 px-4 sm:px-6 border-y border-[#e5dcc4]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366]/15 mb-4">
            <Phone className="w-6 h-6 text-[#25D366]" strokeWidth={1.75} />
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl text-[hsl(var(--text-primary))] mb-3 leading-tight">
            ¿Prefieres hablar antes de decidir?
          </h3>
          <p className="text-[hsl(var(--text-secondary))] text-[15px] sm:text-base max-w-xl mx-auto mb-6 leading-relaxed">
            Si necesitas resolver dudas con calma, escríbeme por WhatsApp y agendamos una llamada — sin compromiso, sin presión.
          </p>
          <a
            href={waLink(WA_CALL_TEXT)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20b658] text-white px-6 py-3 rounded-full text-sm sm:text-base font-medium transition-all shadow-md hover:-translate-y-0.5"
            data-testid="cta-whatsapp-call-mid"
          >
            <MessageCircle className="w-4 h-4" /> Agendar una llamada por WhatsApp
          </a>
          <p className="mt-3 text-xs text-[hsl(var(--text-tertiary))]">+34 654 827 313 · Herminio Vázquez</p>
        </div>
      </section>

      {/* ============ 14. INVERSIÓN ============ */}
      <section id="inversion" className="bg-[#f5efe6] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <SectionEyebrow>Inscríbete en LÍMITES</SectionEyebrow>
            <SectionTitle className="mb-3">Da el paso · Inscríbete en LÍMITES</SectionTitle>
            <p className="text-[hsl(var(--text-secondary))] max-w-xl mx-auto">
              Elige la modalidad de pago que mejor te acompañe. Ambas incluyen acceso completo al proceso, en directo y grabado.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Pago único */}
            <div className="relative bg-white rounded-2xl border-2 border-[#c9a227] p-6 sm:p-8 shadow-lg shadow-[#c9a227]/10">
              <div className="absolute -top-3 left-6 bg-[#c9a227] text-white text-xs font-semibold tracking-wide px-3 py-1 rounded-full">
                Recomendado
              </div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#8a7428] font-medium mb-2">Pago único</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-5xl sm:text-6xl text-[hsl(var(--text-primary))]">1.119,25</span>
                <span className="text-xl text-[hsl(var(--text-primary))]">€</span>
              </div>
              <p className="text-sm text-[#8a7428] mb-6">IVA incluido · 925 € + IVA</p>
              <ul className="space-y-2 mb-6">
                <CheckLi>Acceso completo a LÍMITES</CheckLi>
                <CheckLi>Clase 0 disponible inmediatamente</CheckLi>
                <CheckLi>Todas las sesiones principales y círculos</CheckLi>
                <CheckLi>Grabaciones y materiales</CheckLi>
              </ul>
              <Link
                to={`/checkout/${checkoutSlug}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-6 py-3.5 rounded-full font-medium text-sm sm:text-base transition-all shadow-md shadow-[#c9a227]/25 hover:-translate-y-0.5"
                data-testid="cta-pago-unico"
              >
                Entrar en LÍMITES · Pago único
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-[11px] text-[hsl(var(--text-tertiary))] mt-3 text-center">Pago seguro vía PayPal (tarjeta o cuenta PayPal)</p>
            </div>

            {/* Pago fraccionado */}
            <div className="bg-white rounded-2xl border border-[#e5dcc4] p-6 sm:p-8">
              <p className="text-xs uppercase tracking-[0.18em] text-[#8a7428] font-medium mb-2">Pago fraccionado</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-3xl text-[hsl(var(--text-primary))]">4 ×</span>
                <span className="font-heading text-5xl sm:text-6xl text-[hsl(var(--text-primary))]">326,70</span>
                <span className="text-xl text-[hsl(var(--text-primary))]">€</span>
              </div>
              <p className="text-sm text-[#8a7428] mb-6">IVA incluido · 270 € + IVA por cuota · Total 1.306,80 €</p>
              <ul className="space-y-2 mb-6">
                <CheckLi>Acceso completo al mismo proceso</CheckLi>
                <CheckLi>Clase 0 disponible inmediatamente</CheckLi>
                <CheckLi>Primera cuota al inscribirte, resto automático mensual</CheckLi>
              </ul>
              <Link
                to={`/checkout/${checkoutSlug}?mode=installment`}
                className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-[#faf6ed] text-[#8a7428] px-6 py-3.5 rounded-full font-medium text-sm sm:text-base transition-all border-2 border-[#c9a227] hover:-translate-y-0.5"
                data-testid="cta-pago-fraccionado"
              >
                Entrar en LÍMITES · 4 pagos
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-[11px] text-[hsl(var(--text-tertiary))] mt-3 text-center">Pago seguro vía PayPal · Cuotas automáticas</p>
            </div>
          </div>

          {/* Bizum / transferencia */}
          <div className="bg-white rounded-2xl border border-[#e5dcc4] p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#8a7428]" strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[hsl(var(--text-primary))] mb-1">¿Prefieres Bizum o transferencia?</p>
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  Puedes escribirme directamente y te facilitaré los datos para realizar la inscripción por otra vía. Disponible tanto para pago único (1.119,25 € IVA incluido) como fraccionado (4 × 326,70 € IVA incluido).
                </p>
              </div>
              <a
                href={waLink(waPagoTexto)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20b658] text-white px-5 py-3 rounded-full text-sm font-medium transition-all shadow-md"
                data-testid="cta-whatsapp-bizum"
              >
                <MessageCircle className="w-4 h-4" />
                Pedir por WhatsApp
              </a>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-[hsl(var(--text-tertiary))]">
            Al inscribirte aceptas las condiciones del proceso.
          </p>
        </div>
      </section>

      {/* ============ 15. QUÉ OCURRE DESPUÉS DE PAGAR ============ */}
      <section className="bg-white py-14 sm:py-20 px-4 sm:px-6 border-t border-[#e5dcc4]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <SectionEyebrow>Después de pagar</SectionEyebrow>
            <SectionTitle>Qué ocurre inmediatamente después</SectionTitle>
          </div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              'Tu inscripción queda confirmada.',
              'Recibes acceso al campus del alumnado.',
              'Puedes entrar inmediatamente y ver la Clase 0.',
              'Realizas tu punto de partida antes del recorrido principal.',
              'El martes 13 de octubre a las 17:30 h nos encontramos en directo.',
            ].map((s, i) => (
              <li key={i} className="bg-[#faf6ed] rounded-xl border border-[#e5dcc4] p-5">
                <div className="font-heading text-3xl text-[#c9a227] leading-none mb-3">{String(i + 1).padStart(2, '0')}</div>
                <p className="text-[14px] leading-[1.6] text-[hsl(var(--text-primary))]">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============ 16. FAQ ============ */}
      <section id="faq" className="bg-[#fdfbf5] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <SectionEyebrow>Preguntas frecuentes</SectionEyebrow>
            <SectionTitle>Resuelve tus dudas antes de inscribirte</SectionTitle>
          </div>
          <div>
            {faqs.map((f, i) => (
              <Faq
                key={i}
                q={f.q}
                a={f.a}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                testId={`faq-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ 17. CIERRE ============ */}
      <section className="bg-gradient-to-br from-[#0f1a26] via-[#151f2d] to-[#1a2332] py-16 sm:py-24 px-4 sm:px-6 text-white text-center">
        <div className="max-w-3xl mx-auto">
          <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#e8c75a] font-medium mb-4">
            <span className="w-6 h-px bg-[#c9a227]/60" /> Volver a casa <span className="w-6 h-px bg-[#c9a227]/60" />
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[3rem] leading-[1.15] text-white mb-6">
            LÍMITES.<br />El espacio original de la consciencia.
          </h2>
          <p className="text-white/85 text-lg mb-3 max-w-2xl mx-auto">
            Quizá el límite más profundo no aparezca en el momento en que dices "no". Quizá empiece mucho antes: en el lugar desde el que escuchas, sientes, decides, amas y permaneces.
          </p>
          <p className="font-[Cormorant_Garamond,serif] italic text-2xl sm:text-3xl text-[#e8c75a] my-8 leading-tight">
            "Durante estos tres meses vamos a explorar ese lugar."
          </p>
          <p className="text-[#e8c75a] text-sm tracking-[0.18em] uppercase mb-2">Empezamos el 13 de octubre</p>
          <p className="text-white/60 text-sm mb-6">La Clase 0 ya te está esperando dentro del campus.</p>
          <button
            onClick={() => scrollTo('inversion')}
            className="inline-flex items-center justify-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-4 rounded-full font-medium text-sm sm:text-base transition-all shadow-lg shadow-[#c9a227]/40 hover:-translate-y-0.5"
            data-testid="cta-cierre"
          >
            Entrar en LÍMITES <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </>
  );
}
